import React, { useEffect, useState } from "react";
import { Phone, ArrowRight, Sparkles } from "lucide-react";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { HERO } from "@/constants/testIds";
import { resolveMediaUrl } from "@/lib/api";

export default function Hero({ settings }) {
  const heroImage =
    settings?.hero_image_url ||
    "https://images.unsplash.com/photo-1605164598708-25701594473e?crop=entropy&cs=srgb&fm=jpg&q=85";

  const phoneClean = (settings?.phone || "+1 (619) 769-2398").replace(/[^+\d]/g, "");
  const hasLogo = Boolean(settings?.logo_url);

  // subtle parallax for hero logo
  const [scrollY, setScrollY] = useState(0);
  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <section
      id="hero"
      data-testid={HERO.root}
      className="relative min-h-[100vh] flex items-center overflow-hidden bg-black"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={resolveMediaUrl(heroImage)}
          alt="Auto detailing background"
          className="w-full h-full object-cover"
          style={{ transform: `translate3d(0, ${scrollY * 0.15}px, 0)` }}
        />
        <div className="absolute inset-0 bg-black/75" />
        <div className="absolute inset-0 bg-gradient-to-b from-black via-transparent to-black" />
        {/* radial cyan glow */}
        <div
          aria-hidden
          className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full opacity-30 blur-3xl"
          style={{
            background:
              "radial-gradient(circle at center, #00E5FF 0%, #0055aa 30%, transparent 70%)",
          }}
        />
        <div
          aria-hidden
          className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl"
          style={{
            background:
              "radial-gradient(circle at center, #1e40af 0%, transparent 70%)",
          }}
        />
        {/* subtle noise */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.05] mix-blend-overlay"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
          }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 mx-auto max-w-7xl px-5 md:px-10 pt-32 pb-20 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* LEFT: Text + CTAs */}
          <div className="lg:col-span-7 order-2 lg:order-1">
          <div className="flex items-center gap-3 mb-8 animate-fade-in">
            <span className="h-px w-10 bg-cyan-400" />
            <span className="text-cyan-400 uppercase tracking-[0.35em] text-xs font-medium flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5" />
              {settings?.service_area || "Serving Mandeville, LA"}
            </span>
          </div>

            <h1
              data-testid={HERO.title}
              className="font-heading text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] font-black uppercase tracking-tighter leading-[0.9] text-white"
            >
              {(settings?.hero_title || "PREMIUM AUTO DETAILING & PRESSURE WASHING")
                .split(" ")
                .map((w, i, arr) => (
                  <span key={i}>
                    {i === arr.length - 2 ? (
                      <span className="text-cyan-400">{w} </span>
                    ) : (
                      <>{w} </>
                    )}
                  </span>
                ))}
            </h1>

            <p
              data-testid={HERO.subtitle}
              className="mt-8 max-w-2xl text-lg md:text-xl text-zinc-300 leading-relaxed font-light"
            >
              {settings?.hero_subtitle ||
                "Premium ceramic coatings, showroom-grade detailing and professional pressure washing for homes and businesses in Mandeville, Louisiana."}
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Button
                asChild
                data-testid={HERO.primaryCta}
                className="rounded-none bg-cyan-400 hover:bg-cyan-300 text-black font-bold uppercase tracking-widest text-sm h-14 px-8 shadow-[0_0_28px_rgba(0,229,255,0.35)] group"
              >
                <a
                  href="#contact"
                  onClick={(e) => {
                    e.preventDefault();
                    document
                      .getElementById("contact")
                      ?.scrollIntoView({ behavior: "smooth" });
                  }}
                >
                  Book Your Detail
                  <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
                </a>
              </Button>

              <Button
                asChild
                variant="outline"
                data-testid={HERO.phoneCta}
                className="rounded-none border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-cyan-400 font-medium uppercase tracking-widest text-sm h-14 px-8 backdrop-blur-md"
              >
                <a href={`tel:${phoneClean}`}>
                  <Phone className="mr-2 w-4 h-4" />
                  {settings?.phone || "+1 (619) 769-2398"}
                </a>
              </Button>
            </div>

            <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-8 max-w-3xl">
              {[
                { k: "500+", v: "Vehicles Detailed" },
                { k: "4.9★", v: "Client Rating" },
                { k: "Ceramic", v: "Coatings Certified" },
                { k: "Same Day", v: "Quotes" },
              ].map((s) => (
                <div key={s.v}>
                  <div className="font-heading text-3xl md:text-4xl font-black text-white">
                    {s.k}
                  </div>
                  <div className="text-xs uppercase tracking-widest text-zinc-500 mt-1">
                    {s.v}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT: Dynamic logo showcase */}
          <div
            className="lg:col-span-5 order-1 lg:order-2 relative flex items-center justify-center"
            style={{ transform: `translate3d(0, ${scrollY * -0.05}px, 0)` }}
          >
            <LogoShowcase settings={settings} hasLogo={hasLogo} />
          </div>
        </div>
      </div>

      {/* Scroll hint */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-zinc-500 uppercase tracking-widest text-[10px] animate-bounce-slow">
        <span>Scroll</span>
        <span className="w-px h-8 bg-gradient-to-b from-cyan-400 to-transparent" />
      </div>
    </section>
  );
}

function LogoShowcase({ settings, hasLogo }) {
  return (
    <div className="relative w-full max-w-md mx-auto aspect-square flex items-center justify-center">
      {/* Dark radial vignette so the logo reads well over any background */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at center, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.55) 40%, rgba(0,0,0,0.15) 65%, transparent 80%)",
        }}
      />
      {/* Rotating conic gradient ring */}
      <div className="absolute inset-0 rounded-full opacity-40 animate-spin-slow"
        style={{
          background:
            "conic-gradient(from 0deg, transparent 0deg, #00E5FF 90deg, transparent 180deg, #1e40af 270deg, transparent 360deg)",
          filter: "blur(40px)",
        }}
      />
      {/* Inner pulsing glow */}
      <div
        aria-hidden
        className="absolute inset-8 rounded-full opacity-70 animate-pulse-slow"
        style={{
          background:
            "radial-gradient(circle at center, rgba(0,229,255,0.35) 0%, rgba(30,64,175,0.2) 40%, transparent 70%)",
          filter: "blur(30px)",
        }}
      />
      {/* Static hex accents */}
      <div className="absolute inset-4 border border-white/5 rotate-45 rounded-lg" />
      <div className="absolute inset-10 border border-cyan-400/10 rotate-12 rounded-lg" />

      {/* The logo itself */}
      <div
        className="relative z-10 animate-float"
        data-testid={HERO.logo}
      >
        {hasLogo ? (
          <div className="relative">
            <img
              src={resolveMediaUrl(settings.logo_url)}
              alt="MSJ Car Detailing & Pressure Washing"
              className="w-full h-auto max-w-md object-contain drop-shadow-[0_0_50px_rgba(0,229,255,0.6)]"
            />
            {/* Shine sweep overlay */}
            <div
              aria-hidden
              className="absolute inset-0 pointer-events-none overflow-hidden"
            >
              <div className="absolute inset-y-0 -left-1/2 w-1/2 bg-gradient-to-r from-transparent via-white/15 to-transparent animate-shine skew-x-12" />
            </div>
          </div>
        ) : (
          <Logo size="xl" />
        )}
      </div>
    </div>
  );
}
